import React, { useEffect, useRef } from 'react'
import { useDrop } from 'react-dnd'
import Phaser from 'phaser'
import usePlayStore from '../../stores/playStore'
import FieldScene from '../../scenes/FieldScene'

const Field = () => {
  const gameRef = useRef(null)
  const phaserGameRef = useRef(null)
  const { teams, props, fieldSettings, updateTeam, addTeam, removeTeam, addProp, updateProp, removeProp, animations, currentTime, setSelectedEntity, clearSelectedEntity, selectedEntity } = usePlayStore()

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ['team', 'prop'],
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset()
      if (!offset || !gameRef.current || !phaserGameRef.current) return
      
      const canvas = phaserGameRef.current.canvas
      const scene = phaserGameRef.current.scene.scenes[0]
      if (!canvas || !scene || !scene.screenToWorld) return
      
      // Get screen coordinates relative to canvas
      const canvasRect = canvas.getBoundingClientRect()
      const screenX = offset.x - canvasRect.left
      const screenY = offset.y - canvasRect.top
      
      // Convert to world coordinates using Phaser
      const worldCoords = scene.screenToWorld(screenX, screenY)
      
      if (monitor.getItemType() === 'team') {
        addTeam({
          type: item.type,
          x: worldCoords.x,
          y: worldCoords.y,
          color: item.color,
          number: item.number
        })
      } else if (monitor.getItemType() === 'prop') {
        addProp({
          type: item.type,
          x: worldCoords.x,
          y: worldCoords.y,
          color: item.color
        })
      }
    },
    collect: (monitor) => ({
isOver: monitor.isOver(),
    }),
  }), [addTeam, addProp])

  useEffect(() => {
    if (!gameRef.current) return

    const config = {
      type: Phaser.AUTO,
      width: gameRef.current.offsetWidth,
      height: gameRef.current.offsetHeight,
      parent: gameRef.current,
      backgroundColor: '#22C55E', // Green field color
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0 },
          debug: false
        }
      },
      scene: [FieldScene]
    }

    phaserGameRef.current = new Phaser.Game(config)

    // Set up the callbacks for team updates when scene is ready
    const setupSceneCallbacks = () => {
      if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
        const scene = phaserGameRef.current.scene.scenes[0]
        
        // Listen for scene ready event
        scene.events.on('sceneReady', () => {
          // Set up callbacks first
          scene.setTeamPositionUpdateCallback((teamId, x, y) => {
            updateTeam(teamId, { x, y })
          })
          scene.setTeamRemoveCallback((teamId) => {
            removeTeam(teamId)
          })
          scene.setPropPositionUpdateCallback((propId, x, y) => {
            updateProp(propId, { x, y })
          })
          scene.setPropRemoveCallback((propId) => {
            removeProp(propId)
          })
          scene.setEntitySelectCallback((entity) => {
            setSelectedEntity(entity)
          })
          scene.setCurrentSelectionGetter(() => {
            return usePlayStore.getState().selectedEntity
          })
          
          // Make scene accessible globally for animation recording
          window.phaserScene = scene
        })
        
        // If scene is already ready, set up callbacks immediately
        if (scene.scene.isActive()) {
          scene.events.emit('sceneReady')
        }
      } else {
        // Retry after a short delay
        setTimeout(setupSceneCallbacks, 50)
      }
    }

    // Start the setup process
    setupSceneCallbacks()

    return () => {
      if (phaserGameRef.current) {
        phaserGameRef.current.destroy(true)
        phaserGameRef.current = null
      }
      // Clean up global scene reference
      window.phaserScene = null
    }
  }, [updateTeam, removeTeam, updateProp, removeProp])

  // Update field settings when they change
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      scene.updateFieldSettings(fieldSettings)
    }
  }, [fieldSettings])

  // Update teams when they change (count or content changes)
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      // Update teams if count changed or if team IDs changed (for loading different plays)
      const currentTeamIds = teams.map(t => t.id).sort().join(',')
      const lastTeamIds = scene.lastTeamIds || ''
      
      if (scene.lastTeamCount !== teams.length || currentTeamIds !== lastTeamIds) {
        scene.updateTeams(teams)
        scene.lastTeamCount = teams.length
        scene.lastTeamIds = currentTeamIds
      }
    }
  }, [teams]) // Depend on the entire teams array to catch content changes

  // Update props when they change (count or content changes)
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      // Update props if count changed or if prop IDs changed (for loading different plays)
      const currentPropIds = props.map(p => p.id).sort().join(',')
      const lastPropIds = scene.lastPropIds || ''
      
      if (scene.lastPropCount !== props.length || currentPropIds !== lastPropIds) {
        scene.updateProps(props)
        scene.lastPropCount = props.length
        scene.lastPropIds = currentPropIds
      }
    }
  }, [props]) // Depend on the entire props array to catch content changes

  // Update animations when they change
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      scene.updateAnimations(animations, currentTime)
    }
  }, [animations, currentTime])

  // Update selection visuals when selectedEntity changes
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0]
      if (scene.updateSelectionVisuals) {
        // Small delay to ensure sprites are fully created before updating visuals
        setTimeout(() => {
          scene.updateSelectionVisuals(selectedEntity)
        }, 10)
      }
    }
  }, [selectedEntity])

  return (
    <div className="field-container">
      <div 
        ref={(node) => {
          gameRef.current = node
          drop(node)
        }}
        className="w-full h-full"
      />
    </div>
  )
}

export default Field

